/**
 * Iter52.63 · P4 · Desplegable con buscador.
 * Iter52.63c · C3 · Uso diario: orden por relevancia, teclado completo
 * (primera opción resaltada, flechas, Enter/Tab, Esc) y etiqueta sin
 * repetir el SKU cuando coincide con el nombre.
 *
 * `options`: [valor, etiqueta, extra] — extra = SKU o código Geotab.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Search, X } from 'lucide-react';

// Iter52.63c · C3 · «SMSG SMSG» → «SMSG».
export const etiquetaSinDuplicados = (etiqueta) => {
  const partes = String(etiqueta || '').trim().split(/\s+/);
  const vistas = [];
  partes.forEach(p => {
    if (!vistas.some(v => v.toLowerCase() === p.toLowerCase())) vistas.push(p);
  });
  return vistas.join(' ');
};

// Iter52.63c · C3 · 0 empieza por · 1 contiene en el nombre · 2 SKU/código.
export const relevancia = ([val, label, extra], texto) => {
  const t = texto.toLowerCase();
  const nombre = String(label || '').toLowerCase();
  if (nombre.startsWith(t)) return 0;
  if (nombre.includes(t)) return 1;
  if (`${extra || ''} ${val || ''}`.toLowerCase().includes(t)) return 2;
  return -1;
};

export const SelectBuscador = ({
  value, onChange, options, blank = '', placeholder = 'Selecciona…',
  testid = 'select-buscador',
}) => {
  const [abierto, setAbierto] = useState(false);
  const [q, setQ] = useState('');
  const [idx, setIdx] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    const fuera = (e) => { if (ref.current && !ref.current.contains(e.target)) setAbierto(false); };
    document.addEventListener('mousedown', fuera);
    return () => document.removeEventListener('mousedown', fuera);
  }, []);

  const filtradas = useMemo(() => {
    const t = q.trim();
    if (!t) return options;
    return options
      .map(o => [o, relevancia(o, t)])
      .filter(([, r]) => r >= 0)
      .sort((a, b) => a[1] - b[1])     // estable: mantiene el orden por nombre
      .map(([o]) => o);
  }, [q, options]);

  useEffect(() => { setIdx(0); }, [q]);

  const elegir = (val) => { onChange(val); setAbierto(false); setQ(''); };

  const teclas = (e) => {
    if (e.key === 'Escape') { setAbierto(false); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setIdx(i => Math.min(i + 1, filtradas.length - 1)); return; }
    if (e.key === 'ArrowUp') { e.preventDefault(); setIdx(i => Math.max(i - 1, 0)); return; }
    if (e.key === 'Enter' || e.key === 'Tab') {
      if (filtradas[idx]) { e.preventDefault(); elegir(filtradas[idx][0]); }
    }
  };

  const seleccionada = options.find(([val]) => val === value);

  return <div className="relative" ref={ref} data-testid={testid}>
    <button type="button" onClick={() => { setAbierto(o => !o); setQ(''); }}
            data-testid={`${testid}-toggle`}
            className="input flex items-center justify-between gap-2 text-left w-full">
      <span className={seleccionada ? '' : 'text-slate-400'}>
        {seleccionada ? etiquetaSinDuplicados(seleccionada[1]) : (value || blank || placeholder)}
      </span>
      <ChevronDown className="w-4 h-4 text-slate-400 shrink-0"/>
    </button>
    {abierto && <div className="absolute z-30 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-100">
        <Search className="w-4 h-4 text-slate-400"/>
        <input autoFocus value={q} onChange={e => setQ(e.target.value)} onKeyDown={teclas}
               data-testid={`${testid}-buscar`}
               placeholder="Escribe y pulsa Enter · ↑↓ para moverte"
               className="flex-1 text-sm outline-none"/>
        {q && <button type="button" onClick={() => setQ('')}><X className="w-3.5 h-3.5 text-slate-400"/></button>}
      </div>
      <div className="max-h-60 overflow-auto text-sm">
        {blank && !q && <button type="button" onClick={() => elegir('')}
                                 className="w-full text-left px-3 py-2 hover:bg-slate-50 text-slate-500">{blank}</button>}
        {filtradas.length === 0
          ? <p className="px-3 py-3 text-slate-400">Sin resultados</p>
          : filtradas.map(([val, label, extra], i) => {
            const etiqueta = etiquetaSinDuplicados(label);
            const mostrarExtra = extra && !etiqueta.toLowerCase().includes(String(extra).toLowerCase());
            return <button key={val} type="button" data-testid={`${testid}-opt-${val}`}
                    onMouseEnter={() => setIdx(i)}
                    onClick={() => elegir(val)}
                    className={`w-full text-left px-3 py-2 ${i === idx ? 'bg-emerald-100' : 'hover:bg-slate-50'} ${val === value ? 'font-semibold' : ''}`}>
              {etiqueta}
              {mostrarExtra && <span className="text-xs text-slate-400"> · {extra}</span>}
            </button>;
          })}
      </div>
    </div>}
  </div>;
};
